import { TimePicker } from "antd";
import { ComponentProps, MouseEvent as ReactMouseEvent, useRef, useState } from "react";

type AntTimePickerProps = ComponentProps<typeof TimePicker>;

const TIME_CELL_SELECTOR = ".ant-picker-time-panel-cell";
const TIME_CELL_DISABLED_CLASS = "ant-picker-time-panel-cell-disabled";
const TIME_COLUMN_SELECTOR = ".ant-picker-time-panel-column";

/**
 * Wraps AntD's TimePicker to auto-close once both columns (hour, then
 * minute — every use of this in the schedule drawer is format HH:mm, so
 * exactly two) have been picked, instead of requiring a separate click
 * elsewhere in the drawer to confirm.
 *
 * needConfirm={false} alone only drops the old "OK" button requirement —
 * rc-picker still only commits a "complex" (multi-column) picker's value
 * when its panel CLOSES, and never auto-closes on selection by itself
 * (confirmed by reading rc-picker's SinglePicker source, not guessed).
 *
 * An earlier version of this counted selections via onCalendarChange, which
 * misses a real case: clicking the FIRST column (hour) already combines
 * with rc-picker's own default for the second (e.g. picking "01" defaults
 * minute to "00"), so if the admin then clicks that already-selected "00"
 * cell to confirm it, the resulting value is IDENTICAL to what's already
 * set — and rc-picker's own useInnerValue hook explicitly skips firing
 * onCalendarChange when the new value equals the current one (verified in
 * rc-picker's useRangeValue.js: triggerCalendarChange bails on
 * isSameMergedDates). That click was silently swallowed and never counted.
 *
 * Instead, this counts raw DOM clicks on the panel's own time cells
 * directly (via panelRender), which fire on every click regardless of
 * whether rc-picker considers the value "changed" — the same mechanism
 * TimeColumn.js itself uses internally (its cell onClick calls onChange
 * unconditionally, with no equality check).
 *
 * That raw-click-counting version had its own bug: it counted ANY 2 clicks
 * on ANY cell, not "one click per distinct column" — so 2 clicks confined
 * to a single column (e.g. correcting the hour: click "01", then click
 * "02") closed the panel before the minute was ever touched. Because AntD
 * applies pointer-events:none to the popup the instant `open` flips false,
 * but keeps it visually rendered through its CSS leave-transition, this
 * read exactly as "the second column is visible but clicking does
 * nothing." Fixed by tracking which DISTINCT columns have been clicked
 * (each cell's owning `<ul data-type="hour"|"minute">`, confirmed in
 * rc-picker's TimeColumn.js) in a Set, closing only once both columns have
 * each received a click — not just any 2 clicks total.
 *
 * Each rendered instance owns its own open/selected-columns state, so this
 * is safe to use inside a list (e.g. one picker per day in
 * RecurrenceSelect's "waktu berbeda per hari") without any extra keying.
 */
export const AutoCloseTimePicker = (props: AntTimePickerProps) => {
  const [open, setOpen] = useState(false);
  const selectedColumnsRef = useRef<Set<string>>(new Set());

  const handleOpenChange: NonNullable<AntTimePickerProps["onOpenChange"]> = (nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen) selectedColumnsRef.current.clear();
    props.onOpenChange?.(nextOpen);
  };

  const handlePanelClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const cell = (e.target as HTMLElement).closest(TIME_CELL_SELECTOR);
    if (!cell || cell.classList.contains(TIME_CELL_DISABLED_CLASS)) return;
    const column = cell.closest<HTMLElement>(TIME_COLUMN_SELECTOR);
    const columnType = column?.dataset.type;
    if (!columnType) return;
    selectedColumnsRef.current.add(columnType);
    if (selectedColumnsRef.current.size >= 2) setOpen(false);
  };

  return (
    <TimePicker
      {...props}
      needConfirm={false}
      open={open}
      onOpenChange={handleOpenChange}
      panelRender={(panelNode) => {
        const rendered = props.panelRender ? props.panelRender(panelNode) : panelNode;
        return <div onClick={handlePanelClick}>{rendered}</div>;
      }}
    />
  );
};
